import { useCallback, useEffect, useRef, useState } from 'react';
import { getToken } from '../../utils/tokenManager';

const API = process.env.REACT_APP_BACKEND_URL;
const DEBOUNCE_MS = 400;

/**
 * useCodeName — fetches, validates, and updates the member's Code Name.
 *
 * Encapsulates the debounced availability check so the CodeNameCard
 * component stays under the 200-line frontend gate.
 */
export default function useCodeName() {
  const [currentName, setCurrentName] = useState('');
  const [draft, setDraft] = useState('');
  const [availability, setAvailability] = useState(null); // {valid, available, reason, suggestions}
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }),
    []
  );

  const fetchCurrent = useCallback(async () => {
    const res = await fetch(`${API}/api/users/me/code-name`, { headers: authHeaders() });
    if (!res.ok) return;
    const data = await res.json();
    setCurrentName(data.code_name || '');
    setDraft(data.code_name || '');
  }, [authHeaders]);

  useEffect(() => {
    fetchCurrent();
  }, [fetchCurrent]);

  const check = useCallback(
    async (candidate) => {
      if (!candidate) {
        setAvailability(null);
        return;
      }
      setChecking(true);
      try {
        const q = encodeURIComponent(candidate);
        const res = await fetch(
          `${API}/api/users/me/code-name/availability?candidate=${q}`,
          { headers: authHeaders() }
        );
        if (res.ok) setAvailability(await res.json());
      } finally {
        setChecking(false);
      }
    },
    [authHeaders]
  );

  const onDraftChange = useCallback(
    (raw) => {
      const upper = raw.replace(/\s+/g, ' ').toUpperCase();
      setDraft(upper);
      setError('');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (upper.trim() === currentName) {
        setAvailability(null);
        return;
      }
      debounceRef.current = setTimeout(() => check(upper.trim()), DEBOUNCE_MS);
    },
    [check, currentName]
  );

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/users/me/code-name`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code_name: draft.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Could not save Code Name.');
        return false;
      }
      setCurrentName(data.code_name);
      setDraft(data.code_name);
      setAvailability(null);
      return true;
    } catch (e) {
      setError('Network error. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [authHeaders, draft]);

  const applySuggestion = useCallback(
    (suggestion) => onDraftChange(suggestion),
    [onDraftChange]
  );

  return {
    currentName,
    draft,
    availability,
    checking,
    saving,
    error,
    onDraftChange,
    save,
    applySuggestion,
    isDirty: draft.trim() !== currentName && draft.trim().length > 0,
  };
}
